import React from "react";
import { ShieldAlert, Camera } from "lucide-react";

/**
 * Aviana Waivers — single source of truth.
 *
 * Used on every registration flow (retreats, day retreats, aerial teacher
 * trainings, workshops/classes, and reiki bookings) so the exact language is
 * identical everywhere. Two simple acknowledgement checkboxes — no yes/no
 * radios, no per-photo opt-outs.
 *
 * Props:
 *   liabilityAgreed / photoAgreed  – boolean state
 *   onLiabilityChange / onPhotoChange – setters
 *   subtitle – contextual subtitle shown under the section heading, e.g.
 *     "Required for every reiki, aerial yoga, and yoga registration."
 */
export function Waivers({
  liabilityAgreed,
  onLiabilityChange,
  photoAgreed,
  onPhotoChange,
  subtitle = "Required for every reiki, aerial yoga, and yoga registration at Aviana.",
  testidPrefix = "waiver",
}) {
  return (
    <div className="space-y-6" data-testid={`${testidPrefix}-block`}>
      {subtitle && (
        <p className="text-aviana-muted text-[13px] leading-[1.65] italic max-w-2xl">
          {subtitle}
        </p>
      )}

      {/* ── Liability Waiver ── */}
      <div
        data-field="liability_waiver_agreed"
        className="bg-aviana-cream/70 border border-aviana-border rounded-2xl p-6"
        data-testid={`${testidPrefix}-liability-section`}
      >
        <p className="overline-plum mb-3 flex items-center gap-2">
          <ShieldAlert size={14} /> Waiver &amp; Release of Liability
        </p>
        <div className="max-h-64 overflow-y-auto text-aviana-inkSoft text-[13px] leading-[1.7] space-y-3 pr-3 mb-5 border-l-2 border-aviana-gold/30 pl-4">
          <ol className="list-decimal pl-5 space-y-2">
            <li>I represent that I am physically capable of participating in the yoga or other programming provided by Aviana.</li>
            <li>I understand that yoga and physical exercise can be strenuous and subject to risk of serious injury and I have been fully advised that no exercise, activity, or yoga program should be taken without the consent of a doctor or physician and I am responsible for undertaking to obtain such consent.</li>
            <li>I agree that if I engage in any physical activity, or use Aviana amenity on the premises or off premises, including any sponsored Aviana event, I do so entirely at my own risk.</li>
            <li>Any recommendations for changes in diet, including the use of food supplements, and/or essential oil products are entirely my responsibility and I will undertake to consult a physician prior to undergoing any dietary or food supplement changes.</li>
            <li>I agree that I am voluntarily participating in the activities provided, directly and indirectly, by Aviana and the use of facilities and premises provided and assume all risks of injury, illness, or death.</li>
            <li>I agree that Aviana is not responsible for any loss of, or damage to, personal property.</li>
            <li>I understand that Aviana Yoga may be extremely demanding and I take full responsibility for knowing, monitoring, and acting within my abilities and learning and incorporating any modifications or adaptations necessary to proceed with such activities in a safe and appropriate manner.</li>
            <li>I agree Aviana and its directors, instructors, apprentices, employees, agents, and assigns shall not be liable or responsible for any injuries to me which may occur as a result of (a) my use of all amenities and equipment provided by Aviana and my participation in any activity, class, program, or instruction, (b) the sudden and unforeseen malfunctioning of any equipment, (c) Aviana instruction, training, supervision, or dietary recommendations and (d) my slipping and/or falling while in facility, or on Aviana premises, including adjacent sidewalk areas.</li>
            <li>I acknowledge that I have read this Waiver and Release and understand that it is a <strong>RELEASE OF LIABILITY</strong>.</li>
            <li>I expressly agree to release and discharge Aviana and its directors, officers, employees, agents, affiliates, representatives, successors, assigns, and instructors for any and all claims, causes of action or judgments that may arise out the events noted in Item 8 above and I agree to voluntarily forfeit or waive any right that I may otherwise have to bring a legal action against Aviana for personal injury or property damage. To the extent that statute or case law does not prohibit release for ordinary negligence, this release applies to any ordinary negligence on the part of Aviana, its agents, officers, directors, and employees.</li>
            <li>I expressly agree that this release shall be binding upon my heirs, executors, administrators and assigns.</li>
          </ol>
          <p className="pt-1">
            By signing this Waiver and Release, I acknowledge that I have read this Waiver and Release and
            understand all of its terms. I execute it voluntarily and with full knowledge of its significance.
          </p>
          <div className="pt-3 mt-3 border-t border-aviana-border/50">
            <p className="text-aviana-plum font-medium mb-2">
              If you have any of the below conditions, please consult your physician before participating in an aerial yoga class:
            </p>
            <p className="text-[12.5px]">
              Pregnancy · Glaucoma · Recent surgery (esp. shoulder, eyes, back, hips, hands or wrist) ·
              Heart disease · Very high or low blood pressure · Easy onset vertigo · Osteoporosis/bone weakness ·
              Recent head injury · Cerebral Sclerosis · Propensity for Fainting · Carpal tunnel syndrome ·
              Severe arthritis · Sinusitis or head cold · Hiatal hernia · Disc herniation or acute discogenic
              disease · Recent stroke · Artificial hips · Radiculitis (inflammation of nerve root in spine) ·
              Severe muscle spasms · Botox (within 6 hours)
            </p>
          </div>
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!liabilityAgreed}
            onChange={(e) => onLiabilityChange(e.target.checked)}
            className="mt-1 accent-aviana-plum w-4 h-4"
            data-testid={`${testidPrefix}-liability-checkbox`}
          />
          <span className="flex-1 text-[14px] text-aviana-ink leading-[1.55]">
            I acknowledge that I have read this Waiver &amp; Release of Liability and understand all of its terms.
            <span className="text-aviana-plum"> *</span>
          </span>
        </label>
      </div>

      {/* ── Photo Release ── */}
      <div
        data-field="photo_release_agreed"
        className="bg-aviana-cream/70 border border-aviana-border rounded-2xl p-6"
        data-testid={`${testidPrefix}-photo-section`}
      >
        <p className="overline-plum mb-3 flex items-center gap-2">
          <Camera size={14} /> Photo Release
        </p>
        <div className="max-h-56 overflow-y-auto text-aviana-inkSoft text-[13px] leading-[1.7] space-y-3 pr-3 mb-5 border-l-2 border-aviana-gold/30 pl-4">
          <p>
            I hereby grant permission to the Aviana, and its officers, trustees, employees, agents, students,
            representatives, successors, licensees and assigns to photograph and/or video my image, likeness,
            or depiction and/or that of my minor children (if applicable). I hereby grant permission to the
            Aviana to edit, crop, or retouch such photographs and/or videos, and waive any right to inspect
            the final photographs and/or videos. I hereby consent to and permit photographs and/or videos of
            me and/or those of my minor children to be used by the Aviana worldwide for any purpose, including
            educational and advertisement purposes, and in any medium, including print and electronic. I
            understand that the Aviana may use such photographs and/or videos with or without associating
            names thereto. I further waive any claim for compensation of any kind for the Aviana's use or
            publication of photographs of me and/or those of my minor children (if applicable).
          </p>
          <p>
            I hereby fully and forever discharge and release the Aviana from any claim for damages of any
            kind (including, but not limited to, invasion of privacy; defamation; false light or
            misappropriation of name, likeness or image) arising out of the use or publication of photographs
            and/or videos of me and/or those of my minor children (if applicable) by the Aviana, and covenant
            and agree not to sue or otherwise initiate legal proceedings against the Aviana for such use or
            publication on my own behalf or on behalf of my minor children. All grants of permission and
            consent, and all covenants, agreements and understandings contained herein are irrevocable.
          </p>
          <p>
            I acknowledge and represent that I am <strong>over the age of 18</strong>, have read this entire
            document, that I understand its terms and provisions, and that I have signed it knowingly and
            voluntarily on behalf of myself and/or my minor children (if applicable).
          </p>
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!photoAgreed}
            onChange={(e) => onPhotoChange(e.target.checked)}
            className="mt-1 accent-aviana-plum w-4 h-4"
            data-testid={`${testidPrefix}-photo-checkbox`}
          />
          <span className="flex-1 text-[14px] text-aviana-ink leading-[1.55]">
            I acknowledge that I have read this Photo Release and understand all of its terms.
            <span className="text-aviana-plum"> *</span>
          </span>
        </label>
      </div>
    </div>
  );
}
